import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const images = [
  {
    src: "/image (2).jpg",
    alt: "African Investment Club Meeting",
    caption: "Join our monthly investment meetings"
  },
  {
    src: "/image.jpg",
    alt: "Successful investment portfolio",
    caption: "Access high-potential investment opportunities"
  },
  {
    src: "/image (1).jpg",
    alt: "Members celebrating returns",
    caption: "Share knowledge, risk, and rewards"
  }
];

const marqueeItems = [
  "Join our monthly investment meetings",
  "Access high-potential investment opportunities",
  "Share knowledge, risk, and rewards"
];

function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const timeoutRef = useRef(null);
  const delay = 5000;

  // Parallax effect using scroll
  const { scrollY } = useScroll();
  const yParallax = useTransform(scrollY, [0, 300], [0, -50]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const resetTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  useEffect(() => {
    resetTimeout();
    if (isAutoPlaying) {
      timeoutRef.current = setTimeout(
        () =>
          setCurrentSlide((prevIndex) =>
            prevIndex === images.length - 1 ? 0 : prevIndex + 1
          ),
        delay
      );
    }

    return () => {
      resetTimeout();
    };
  }, [currentSlide, isAutoPlaying]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), delay * 2);
  };

  const textVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const buttonVariants = {
    initial: { 
      scale: 1,
      boxShadow: "0 2px 8px rgba(224, 46, 46, 0.3)",
      backgroundColor: "#E02E2E"
    },
    hover: {
      scale: 1.05,
      backgroundColor: "#C02626",
      boxShadow: "0 8px 20px rgba(224, 46, 46, 0.5), 0 0 20px rgba(224, 46, 46, 0.3)",
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    tap: {
      scale: 0.98,
      boxShadow: "0 1px 4px rgba(224, 46, 46, 0.3)"
    },
    pulse: {
      scale: [1, 1.05, 1],
      boxShadow: [
        "0 2px 8px rgba(224, 46, 46, 0.3)",
        "0 4px 16px rgba(224, 46, 46, 0.4), 0 0 20px rgba(224, 46, 46, 0.4)",
        "0 2px 8px rgba(224, 46, 46, 0.3)"
      ],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const marqueeVariants = {
    animate: {
      x: ["0%", "-100%"],
      transition: {
        x: {
          repeat: Infinity,
          repeatType: "loop",
          duration: 20,
          ease: "linear"
        }
      }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 1.1 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeInOut"
      }
    },
    hover: {
      scale: 1.05,
      transition: {
        duration: 0.3
      }
    }
  };

  const marqueeItemVariants = {
    animate: {
      y: [0, -5, 0],
      transition: {
        y: {
          repeat: Infinity,
          repeatType: "loop",
          duration: 2,
          ease: "easeInOut"
        }
      }
    }
  };

  const isMobile = windowWidth <= 768;

  return (
    <>
      {/* Marquee Banner */}
      <div style={{
        marginTop: 60,
        backgroundColor: "#E02E2E",
        marginRight: -5,
        marginLeft: -5,
        color: "#1F1F1F",
        padding: "10px 0",
        overflow: "hidden",
        whiteSpace: "nowrap",
        fontWeight: "600",
        fontSize: isMobile ? "0.9rem" : "1.1rem",
        fontFamily: "Poppins, sans-serif"
      }}>
        <motion.div
          style={{ display: "inline-block" }}
          variants={marqueeVariants}
          animate="animate"
        >
          {[...marqueeItems, ...marqueeItems].map((item, index) => (
            <motion.span
              key={index}
              style={{ margin: "0 40px", display: "inline-block" }}
              variants={marqueeItemVariants}
              animate="animate"
            >
              {item} • 
            </motion.span>
          ))}
        </motion.div>
      </div>

      <section
        id="home"
        style={{
          padding: isMobile ? '100px 20px' : '100px 20px',
          backgroundColor: 'rgb(253, 251, 227)',
          display: 'flex',
          marginRight: -5,
          marginLeft: -5,
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: isMobile ? 'auto' : '100vh',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}
      >
        <div style={{ 
          maxWidth: '1200px',
          width: '100%',
          display: 'flex',
          flexDirection: isMobile ? 'column-reverse' : 'row',
          alignItems: 'center',
          gap: isMobile ? '30px' : '60px',
        }}>
          {/* Text Content */}
          <motion.div 
            style={{
              flex: 1,
              textAlign: isMobile ? 'center' : 'left',
              padding: isMobile ? '0' : '0',
              y: yParallax
            }}
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <motion.h1
              style={{
                fontSize: isMobile ? '1.8rem' : '3rem',
                fontWeight: '700',
                color: '#1F1F1F',
                marginBottom: '20px',
                fontFamily: 'Poppins, sans-serif',
                lineHeight: '1.2',
              }}
              variants={textVariants}
            >
              Invest Together, Grow Together
            </motion.h1>
            
            <motion.h2
              style={{
                fontSize: isMobile ? '1.1rem' : '1.5rem',
                fontWeight: '400',
                color: '#4B5563',
                marginBottom: '24px',
                fontFamily: 'Poppins, sans-serif',
                lineHeight: '1.4',
              }}
              variants={textVariants}
            >
              Empowering Africans Worldwide to Build Wealth Hand in Hand
            </motion.h2>
            
            <motion.div 
              style={{
                height: '1px',
                backgroundColor: '#D1D5DB',
                width: isMobile ? '60px' : '100px',
                margin: isMobile ? '15px auto' : '24px 0',
              }}
              variants={textVariants}
            />
            
            <motion.p
              style={{
                fontSize: isMobile ? '0.95rem' : '1.125rem',
                color: '#4B5563',
                marginBottom: isMobile ? '24px' : '32px',
                fontFamily: 'Poppins, sans-serif',
                lineHeight: '1.6',
                maxWidth: '100%',
              }}
              variants={textVariants}
            >
              Welcome to the African Investment Club (AIC)—where global African unity
              meets smart, secure investing. Every month, our members pool resources
              to access high-potential opportunities, from real estate to UAE Initial
              Public Offerings (IPOs), while sharing knowledge, risk, and rewards.
            </motion.p>
            
            <motion.div 
              style={{
                display: 'flex',
                justifyContent: isMobile ? 'center' : 'flex-start',
                gap: '20px',
                flexWrap: 'wrap'
              }}
              variants={textVariants}
            >
              <motion.a
                href="https://t.me/AICUAE"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  color: '#FFFFFF',
                  padding: isMobile ? '12px 24px' : '14px 28px',
                  borderRadius: '8px',
                  fontSize: isMobile ? '0.95rem' : '1.125rem',
                  fontWeight: '600',
                  textDecoration: 'none',
                  fontFamily: 'Poppins, sans-serif',
                  boxShadow: '0 2px 8px rgba(224, 46, 46, 0.3)',
                  border: 'none',
                  cursor: 'pointer'
                }}
                variants={buttonVariants}
                initial="initial"
                animate="pulse"
                whileHover="hover"
                whileTap="tap"
              >
                Join the Club
              </motion.a>
            </motion.div>
          </motion.div>

          {/* Image Carousel */}
          <div style={{
            flex: 1,
            marginTop: -70,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: isMobile ? '0' : '0',
            position: 'relative',
            width: isMobile ? '100%' : 'auto'
          }}>
            <div style={{
              width: '100%',
              maxWidth: isMobile ? '350px' : '500px',
              height: 'auto',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              aspectRatio: '1/1',
              position: 'relative'
            }}>
              {images.map((image, index) => (
                <motion.img
                  key={index}
                  src={image.src}
                  alt={image.alt}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    position: 'absolute',
                    top: 0,
                    left: 0
                  }}
                  variants={imageVariants}
                  initial="hidden"
                  animate={index === currentSlide ? "visible" : "hidden"}
                  whileHover="hover"
                />
              ))}
              
              {/* Image caption */}
              <motion.div
                style={{
                  position: 'absolute',
                  bottom: '0',
                  left: '0',
                  right: '0',
                  textAlign: 'center',
                  color: 'white',
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  padding: '12px',
                  borderRadius: '0 0 8px 8px',
                  fontSize: isMobile ? '0.85rem' : '1rem'
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ 
                  opacity: 1,
                  y: 0,
                  transition: { delay: 0.2, duration: 0.4 }
                }}
              >
                {images[currentSlide].caption}
              </motion.div>
            </div>
            
            {/* Carousel indicators */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '20px',
              gap: '8px'
            }}>
              {images.map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => goToSlide(index)}
                  style={{
                    width: isMobile ? '10px' : '12px',
                    height: isMobile ? '10px' : '12px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: currentSlide === index ? '#E02E2E' : '#D1D5DB',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease',
                    padding: 0
                  }}
                  aria-label={`Go to slide ${index + 1}`}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                />
              ))}
            </div>
            
            {/* Navigation arrows - Mobile */}
            {isMobile && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '20px',
                marginTop: '15px',
                width: '100%'
              }}>
                <motion.button
                  onClick={() => {
                    setCurrentSlide(prev => (prev === 0 ? images.length - 1 : prev - 1));
                    setIsAutoPlaying(false);
                    setTimeout(() => setIsAutoPlaying(true), delay * 2);
                  }}
                  style={{
                    background: 'rgba(231, 229, 218, 0.9)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                  }}
                  aria-label="Previous slide"
                  whileHover={{ scale: 1.1, boxShadow: '0 4px 10px rgba(224, 46, 46, 0.3)' }}
                  whileTap={{ scale: 0.9 }}
                >
                  <span style={{ fontSize: '20px', color: '#E02E2E' }}>❮</span>
                </motion.button>
                
                <motion.button
                  onClick={() => {
                    setCurrentSlide(prev => (prev === images.length - 1 ? 0 : prev + 1));
                    setIsAutoPlaying(false);
                    setTimeout(() => setIsAutoPlaying(true), delay * 2);
                  }}
                  style={{
                    background: 'rgba(255,255,255,0.9)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                  }}
                  aria-label="Next slide"
                  whileHover={{ scale: 1.1, boxShadow: '0 4px 10px rgba(224, 46, 46, 0.3)' }}
                  whileTap={{ scale: 0.9 }}
                >
                  <span style={{ fontSize: '20px', color: '#E02E2E' }}>❯</span>
                </motion.button>
              </div>
            )}
            
            {/* Navigation arrows - Desktop */}
            {!isMobile && (
              <>
                <motion.button
                  onClick={() => {
                    setCurrentSlide(prev => (prev === 0 ? images.length - 1 : prev - 1));
                    setIsAutoPlaying(false);
                    setTimeout(() => setIsAutoPlaying(true), delay * 2);
                  }}
                  style={{
                    position: 'absolute',
                    left: '-40px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(255,255,255,0.7)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                    zIndex: 1
                  }}
                  aria-label="Previous slide"
                  whileHover={{ scale: 1.1, boxShadow: '0 4px 10px rgba(224, 46, 46, 0.3)' }}
                  whileTap={{ scale: 0.9 }}
                >
                  <span style={{ fontSize: '20px', color: '#E02E2E' }}>❮</span>
                </motion.button>
                
                <motion.button
                  onClick={() => {
                    setCurrentSlide(prev => (prev === images.length - 1 ? 0 : prev + 1));
                    setIsAutoPlaying(false);
                    setTimeout(() => setIsAutoPlaying(true), delay * 2);
                  }}
                  style={{
                    position: 'absolute',
                    right: '-40px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(255,255,255,0.7)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                    zIndex: 1
                  }}
                  aria-label="Next slide"
                  whileHover={{ scale: 1.1, boxShadow: '0 4px 10px rgba(224, 46, 46, 0.3)' }}
                  whileTap={{ scale: 0.9 }}
                >
                  <span style={{ fontSize: '20px', color: '#E02E2E' }}>❯</span>
                </motion.button>
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;